import type { ManagementTimelineQuery } from '@internal/spassu-code-contract';
import type { Dispatch } from 'react';
import { useCallback, useRef } from 'react';
import type { CockpitDataSource } from './CockpitDataSource';
import { asCockpitError } from './cockpitControllerErrors';
import type { CockpitControllerAction } from './cockpitControllerTypes';
import { CockpitRequestCoordinator } from './cockpitRequestCoordinator';
import { validateActivity } from './useCockpitSnapshotLoader';

interface TimelineLoaderContext {
  activityId: string;
  dataSource: CockpitDataSource;
  dispatch: Dispatch<CockpitControllerAction>;
  requests: CockpitRequestCoordinator;
}

export function useCockpitTimelineLoader(
  context: Omit<TimelineLoaderContext, 'requests'>,
) {
  const requests = useRef(new CockpitRequestCoordinator()).current;
  const { activityId, dataSource, dispatch } = context;
  const loadTimeline = useCallback(
    (query: ManagementTimelineQuery, reset: boolean) =>
      requestTimeline(
        { activityId, dataSource, dispatch, requests },
        query,
        reset,
      ),
    [activityId, dataSource, dispatch, requests],
  );
  return { loadTimeline, requests };
}

async function requestTimeline(
  context: TimelineLoaderContext,
  query: ManagementTimelineQuery,
  reset: boolean,
): Promise<void> {
  const request = context.requests.begin();
  context.dispatch({ type: 'timelineStarted', reset });
  try {
    const page = await context.dataSource.getTimeline({
      ...query,
      activityId: context.activityId,
      signal: request.signal,
    });
    validateActivity(page.activityId, context.activityId, 'timeline');
    if (!context.requests.succeed(request, Date.now())) return;
    context.dispatch({ type: 'timelineSucceeded', page, reset });
  } catch (reason) {
    if (!context.requests.isCurrent(request)) return;
    context.requests.finish(request);
    context.dispatch({
      type: 'timelineFailed',
      error: asCockpitError(reason),
    });
  }
}
